import React, { useRef } from "react";
import { Button, Grid, TextField, Typography } from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { arrayMove } from "@dnd-kit/sortable";

const gridStyle = { padding: "8px" };

function SortableRow({ id, record, idx, onChange, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    display: "flex",
    alignItems: "center",
    width: "100%",
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div
        {...attributes}
        {...listeners}
        style={{ cursor: "grab", display: "flex", alignItems: "center", padding: "0 4px" }}
      >
        <DragIndicatorIcon style={{ color: "grey" }} />
      </div>
      <Grid item xs={5} style={gridStyle} sx={{ flex: "0 0 41.66%" }}>
        <TextField
          label="Event Title"
          value={record.title ?? ""}
          onChange={(e) => onChange(idx, { title: e.target.value })}
          fullWidth
        />
      </Grid>
      <Grid item xs={5} style={gridStyle} sx={{ flex: "0 0 41.66%" }}>
        <TextField
          label="Placings (separate by comma)"
          value={record.placings?.join(",") ?? ""}
          onChange={(e) => onChange(idx, { placings: e.target.value.split(",") })}
          fullWidth
        />
      </Grid>
      <Grid
        item
        xs={2}
        style={{ display: "flex", justifyContent: "center", alignItems: "center" }}
        sx={{ flex: "0 0 16.66%" }}
      >
        <Button onClick={() => onDelete(idx)}>Delete</Button>
      </Grid>
    </div>
  );
}

export default function CompetitionHighlights({ records, onChange, previousCompHistory }) {
  const idsRef = useRef(null);

  // Initialize ids array once, then keep it in sync
  if (idsRef.current === null) {
    idsRef.current = (records ?? []).map(() => crypto.randomUUID());
  }
  // If records grew (e.g. loaded after mount), backfill ids
  while (idsRef.current.length < (records ?? []).length) {
    idsRef.current.push(crypto.randomUUID());
  }

  const ids = idsRef.current;

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(active.id);
    const newIndex = ids.indexOf(over.id);
    idsRef.current = arrayMove(ids, oldIndex, newIndex);
    onChange(arrayMove(records, oldIndex, newIndex));
  };

  const handleChange = (idx, patch) => {
    const updated = records.map((r, i) => {
      if (i !== idx) return r;
      return { ...r, ...patch };
    });
    onChange(updated);
  };

  const handleDelete = (idx) => {
    idsRef.current = ids.filter((_, i) => i !== idx);
    onChange(records.filter((_, i) => i !== idx));
  };

  const handleAdd = () => {
    idsRef.current = [...ids, crypto.randomUUID()];
    onChange([...(records ?? []), { title: "", placings: [] }]);
  };

  return (
    <>
      <Grid item xs={12}>
        <Typography variant="h6" style={gridStyle} align="center">
          Click "add another record" for each event you want to add details about.
          Drag rows to reorder.
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            {(records ?? []).map((record, idx) => (
              <SortableRow
                key={ids[idx]}
                id={ids[idx]}
                record={record}
                idx={idx}
                onChange={handleChange}
                onDelete={handleDelete}
              />
            ))}
          </SortableContext>
        </DndContext>
      </Grid>
      <Grid item xs={12}>
        <Button fullWidth onClick={handleAdd}>
          add another record
        </Button>
      </Grid>
      {previousCompHistory && (
        <Grid item xs={12} style={{ paddingLeft: "16px" }}>
          <Typography variant="h6">
            Old Records for Reference **THESE WILL BE LOST ON NEXT SAVE**
          </Typography>
          <Typography variant="body1" color="secondary">
            {previousCompHistory}
          </Typography>
        </Grid>
      )}
    </>
  );
}
